<template>
    <div class="mx-2 flex flex-col gap-1 py-2" v-cloak>

        <div v-if="fetching" class="my-1 flex items-center justify-center gap-2">
            <span class="text-sm text-gray-100">Fetching...</span>
            <spinner />
        </div>

        <div v-if="!fetching" class="flex flex-col gap-1">

            <div class="uppercase text-xs font-semibold mb-2 relative flex items-center justify-center h-6">
                <span>Tickets</span>
                <button @click.prevent="newTicket"
                    class="absolute right-0 top-0 bg-gray-800 text-white rounded-full flex items-center justify-center h-6 w-6">
                    <i v-if="showAddTicket" class="fas fa-times text-xs"></i>
                    <i v-else class="fas fa-plus text-xs"></i>
                </button>
            </div>

            <div v-show="showAddTicket" class=" bg-black/50 rounded p-4 mb-4 flex flex-col border border-jackpot-bg">
                <div class="text-center uppercase text-sm font-bold mb-4">Add Ticket</div>
                <div class="flex flex-col items-start gap-1 mb-2">
                    <label class="text-xs text-gray-400 uppercase px-1">Wallet</label>
                    <input class="bg-white text-black p-1 rounded text-sm w-full outline-0" v-model.trim="ticket.wallet">
                    <div v-if="ticket.error.wallet" class="px-1 text-xs text-red-500">{{ ticket.error.wallet }}</div>
                </div>
                <div class="flex flex-col items-start gap-1 mb-4">
                    <label class="text-xs text-gray-400 uppercase px-1">Race</label>
                    <select v-model="ticket.type" class="text-black p-1 rounded text-sm outline-0">
                        <option value="brass">Brass</option>
                        <option value="apex">Apex</option>
                        <option value="burnout">Burnout</option>
                        <option value="torque">Torque</option>
                        <option value="shakedown">Shakedown</option>
                    </select>
                </div>
                <div class="flex justify-end">
                    <button :disabled="adding" class="modal-action-button" @click.prevent="addTicket">Add</button>
                </div>
            </div>

            <ul class="mb-2 flex flex-col gap-1">
                <li v-for="(item, index) in tickets" class="flex flex-col py-1 px-2 rounded text-sm"
                    :class="index % 2 == 0 ? 'bg-black bg-opacity-10 dark:bg-white dark:bg-opacity-10' : 'dark:bg-white dark:bg-opacity-5'">
                    <div class="flex items-center justify-between">
                        <div class="flex items-center gap-2">
                            <div class="font-semibold">{{ item.wallet_short }}</div>
                            <div v-if="item.user !== null && item.user.name !== item.wallet_short"
                                class="text-sm text-gray-400">{{ item.user.name }}</div>
                            <div v-if="item.user === null" class="uppercase text-xs text-red-500">New User</div>
                        </div>
                        <div class="flex items-center gap-1">
                            <div class="text-sm text-gray-400">{{ item.type.charAt(0).toUpperCase() + item.type.slice(1) }}
                            </div>
                            <div class="text-xs">x</div>
                            <div class="font-semibold">{{ item.amount }}</div>
                        </div>
                    </div>
                </li>
            </ul>

            <div v-if="showPagination" class="flex items-center justify-between">
                <button @click.prevent="prev"
                    class="bg-gray-800 text-white disabled:bg-opacity-50 disabled:text-gray-600 rounded-full flex items-center justify-center h-6 w-6"
                    :disabled="prev_page_url === null"><i class="fas fa-chevron-left text-xs"></i></button>
                <button @click.prevent="next"
                    class="bg-gray-800 text-white disabled:bg-opacity-50 disabled:text-gray-600 rounded-full flex items-center justify-center h-6 w-6"
                    :disabled="next_page_url === null"><i class="fas fa-chevron-right text-xs"></i></button>
            </div>

        </div>
    </div>
</template>

<script>
import axios from 'axios'
import solana from '../../mixins/solana'

export default {
    name: 'AdminSubPanelTickets',
    mixins: [solana],
    props: ['token', 'page'],
    data() {
        return {
            adding: false,
            fetching: false,
            current_page: null,
            next_page_url: null,
            prev_page_url: null,
            tickets: [],
            showAddTicket: false,
            ticket: {
                wallet: '',
                type: 'brass',
                error: {
                    wallet: ''
                }
            }
        }
    },
    computed: {
        showPagination() {
            return this.next_page_url !== null || this.prev_page_url !== null
        }
    },
    watch: {
        current_page(value) {
            this.fetchData(value)
        }
    },
    methods: {
        newTicket() {
            this.showAddTicket = !this.showAddTicket
            if (!this.showAddTicket) {
                this.ticket.wallet = ''
                this.ticket.type = 'brass'
                this.ticket.error.wallet = ''
            }
        },
        addTicket() {
            this.adding = true
            this.ticket.error.wallet = ''
            if (!this.isValidSolanaAddress(this.ticket.wallet)) {
                this.ticket.error.wallet = 'Invalid Wallet!'
                this.adding = false
                return
            }
            const data = {
                wallet: this.ticket.wallet,
                type: this.ticket.type
            }
            axios.post('/api/admin/tickets/submit', data, {
                headers: {
                    'Authorization': `Bearer ${this.token}`
                }
            }).then((response) => {
                this.$toast.success(response.data.message)
            }).catch((err) => {
                this.$toast.error('Error adding Ticket.')
            }).finally(() => {
                this.ticket.wallet = ''
                this.ticket.type = 'brass'
                this.ticket.error.wallet = ''
                this.adding = false
                this.showAddTicket = false
                this.fetchData()
            })
        },
        prev() {
            this.current_page--;
        },
        next() {
            this.current_page++;
        },
        fetchData(page = 1) {
            this.fetching = true
            axios.get('/api/admin/tickets/index?page=' + page, {
                headers: {
                    'Authorization': `Bearer ${this.token}`
                }
            }).then((response) => {
                this.tickets = response.data.data
                this.current_page = response.data.current_page
                this.next_page_url = response.data.next_page_url
                this.prev_page_url = response.data.prev_page_url
            }).catch(() => {
                this.$toast.error('Error fetching Tickets.')
            }).finally(() => {
                this.fetching = false
            })
        }
    },
    mounted() {
        this.current_page = this.page
    }
}
</script>
